import {
  Component,
  EventEmitter,
  Inject,
  Output,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectComponent } from '../../../atoms/select/select.component';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Subscription,
} from 'rxjs';
import { ListingsService } from '@out-web/core';

@Component({
  selector: 'out-hero-filter',
  imports: [RouterLink, CommonModule, ReactiveFormsModule, SelectComponent],
  templateUrl: './hero-filter.component.html',
  styleUrl: './hero-filter.component.scss',
})
export class HeroFilterComponent implements OnInit {
  @Output() tabChanged = new EventEmitter<number>();
  searchForm: FormGroup;
  searchUrl = '';
  isLoading$ = new BehaviorSubject<boolean>(false);
  private subscription: Subscription = new Subscription();

  options = {
    model: [{ label: 'Model', value: 'ALL_OPTIONS' }],
    year: [{ label: 'Year', value: 'ALL_OPTIONS' }],
    location: [{ label: 'Location', value: 'ALL_OPTIONS' }],
  };

  tabs = [{ label: 'Shop Used Tesla' }, { label: 'Sell My Tesla' }];
  selectedTab = 0;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private listingsService: ListingsService,
    public auth: AuthService,
    @Inject(DOCUMENT) public document: Document,
    private cdr: ChangeDetectorRef
  ) {
    this.searchForm = this.fb.group({
      vehicle: this.fb.group({
        model: ['ALL_OPTIONS'],
        year: ['ALL_OPTIONS'],
        location: ['ALL_OPTIONS'],
      }),
    });
  }

  ngOnInit(): void {
    this.loadOptions();

    this.subscription.add(
      this.searchForm.valueChanges
        .pipe(
          debounceTime(200),
          distinctUntilChanged(
            (prev, curr) =>
              JSON.stringify(prev['vehicle']) ===
              JSON.stringify(curr['vehicle'])
          )
        )
        .subscribe(() => {
          this.loadOptions();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadOptions() {
    this.isLoading$.next(true);
    const cleanedFilter = this.cleanFilter(this.searchForm.value);

    const currentModel = this.searchForm.value.vehicle.model;
    const currentYear = this.searchForm.value.vehicle.year;
    const currentLocation = this.searchForm.value.vehicle.location;

    this.subscription.add(
      this.listingsService.fetchFilters(cleanedFilter, true).subscribe({
        next: (filters) => {
          this.options.model = [
            { label: 'Model', value: 'ALL_OPTIONS' },
            ...(filters['model'] || []),
          ];
          this.options.year = [
            { label: 'Year', value: 'ALL_OPTIONS' },
            ...(filters['year'] || []),
          ];
          this.options.location = [
            { label: 'Location', value: 'ALL_OPTIONS' },
            ...(filters['location'] || []),
          ];

          this.cdr.detectChanges();

          if (
            this.options.model.some((option) => option.value === currentModel)
          ) {
            this.getControl('model').setValue(currentModel, {
              emitEvent: false,
            });
          } else {
            this.getControl('model').setValue('ALL_OPTIONS', {
              emitEvent: false,
            });
          }

          if (
            this.options.year.some((option) => option.value === currentYear)
          ) {
            this.getControl('year').setValue(currentYear, { emitEvent: false });
          } else {
            this.getControl('year').setValue('ALL_OPTIONS', {
              emitEvent: false,
            });
          }

          if (
            this.options.location.some(
              (option) => option.value === currentLocation
            )
          ) {
            this.getControl('location').setValue(currentLocation, {
              emitEvent: false,
            });
          } else {
            this.getControl('location').setValue('ALL_OPTIONS', {
              emitEvent: false,
            });
          }

          this.isLoading$.next(false);
        },
        error: () => {
          this.isLoading$.next(false);
        },
      })
    );
  }

  goToSearchUrl(): void {
    const { model, year, location } = this.searchForm.value.vehicle;
    const queryParams: { model?: string; year?: string; location?: string } =
      {};

    if (model !== 'ALL_OPTIONS') queryParams.model = model;
    if (year !== 'ALL_OPTIONS') queryParams.year = year;
    if (location !== 'ALL_OPTIONS') queryParams.location = location;

    this.router.navigate(['used-tesla-for-sale'], { queryParams });
  }

  getControl(controlName: string): FormControl {
    return this.searchForm.get(`vehicle.${controlName}`) as FormControl;
  }

  private cleanFilter(filter: { vehicle: { [key: string]: string } }) {
    const cleaned: { [key: string]: string } = {};
    const vehicleKeys = Object.keys(filter['vehicle']);

    vehicleKeys.forEach((key) => {
      if (filter['vehicle'][key] !== 'ALL_OPTIONS') {
        cleaned[key] = filter['vehicle'][key];
      }
    });

    if (!cleaned) {
      return {};
    }

    return cleaned;
  }

  selectTab(index: number) {
    this.selectedTab = index;
    this.tabChanged.emit(index);
  }
}
